/**
 * Scientific Calculator — floating panel used inside experiment observation tables.
 * Safe expression evaluator (no eval): sanitizes input and runs via new Function()
 * with a controlled Math scope. Supports: + - * / ( ) . digits, √, x², x^y, sin/cos/tan
 * (deg or rad), log, ln, π, e, %, backspace, clear, and a copy-result button.
 */
import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Calculator as CalcIcon, X, Copy, Delete, ChevronsRight } from "lucide-react";

const OPERATORS = new Set(["+", "-", "*", "/", "(", ")", ".", "^", "%", ","]);

/** Convert a friendly infix expression to something new Function can evaluate. */
const toEvaluable = (expr, mode /* 'deg'|'rad' */) => {
  let s = expr
    .replace(/π/g, "Math.PI")
    .replace(/(?<![a-zA-Z_])e(?![a-zA-Z0-9])/g, "Math.E")
    .replace(/√\(/g, "Math.sqrt(")
    .replace(/√/g, "Math.sqrt")
    .replace(/\^/g, "**")
    .replace(/(\d)\s*%\s*(\d)/g, "($1/100*$2)")
    .replace(/%\)/g, "/100)");

  // Trig — if user is in degrees, wrap the argument in a deg→rad conversion.
  const trigNames = ["sin", "cos", "tan", "asin", "acos", "atan"];
  trigNames.forEach((n) => {
    const re = new RegExp(`(?<![a-zA-Z_])${n}\\(`, "g");
    if (mode === "deg" && !n.startsWith("a")) {
      s = s.replace(re, `Math.${n}((Math.PI/180)*(`);
    } else if (mode === "deg" && n.startsWith("a")) {
      s = s.replace(re, `((180/Math.PI)*Math.${n}(`);
    } else {
      s = s.replace(re, `Math.${n}(`);
    }
  });
  s = s.replace(/(?<![a-zA-Z_])log\(/g, "Math.log10(");
  s = s.replace(/(?<![a-zA-Z_])ln\(/g, "Math.log(");

  // Balance parens roughly — closing parens for the trig-deg wraps we added
  if (mode === "deg") {
    const opens = (s.match(/\(\(Math\.PI\/180\)\*\(/g) || []).length;
    const closes = (s.match(/\)\)/g) || []).length;
    if (opens > 0) {
      // add one extra ')' per opened deg wrapper if not already balanced
      const need = opens - Math.min(opens, closes);
      for (let i = 0; i < need; i++) s += ")";
    }
    const openAsin = (s.match(/\(\(180\/Math\.PI\)\*Math\.a[a-z]+\(/g) || []).length;
    for (let i = 0; i < openAsin; i++) s += ")";
  }
  return s;
};

const evaluateSafe = (expr, mode) => {
  if (!expr.trim()) return "";
  try {
    const code = toEvaluable(expr, mode);
    // eslint-disable-next-line no-new-func
    const fn = new Function(`"use strict"; return (${code});`);
    const val = fn();
    if (typeof val !== "number" || !isFinite(val)) return "Err";
    // Trim to 8 significant digits
    return Number(val.toPrecision(10)).toString();
  } catch {
    return "Err";
  }
};

const Btn = ({ children, onClick, cls = "", testid }) => (
  <button
    type="button"
    onClick={onClick}
    className={`h-11 rounded-lg font-mono text-base transition-all active:scale-95 ${cls}`}
    data-testid={testid}
  >
    {children}
  </button>
);

export const Calculator = ({ open, onClose, formulas }) => {
  const [expr, setExpr] = useState("");
  const [preview, setPreview] = useState("");
  const [mode, setMode] = useState("deg"); // deg | rad
  const [history, setHistory] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    setPreview(evaluateSafe(expr, mode));
  }, [expr, mode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const append = (t) => {
    setExpr((prev) => prev + t);
    inputRef.current?.focus();
  };
  const back = () => setExpr((prev) => prev.slice(0, -1));
  const clear = () => setExpr("");
  const equals = () => {
    if (preview && preview !== "Err") {
      setHistory((h) => [{ expr, value: preview }, ...h].slice(0, 6));
      setExpr(preview);
    }
  };
  const copyValue = async () => {
    if (preview && preview !== "Err") {
      try { await navigator.clipboard.writeText(preview); } catch (err) { console.warn("copy failed", err); }
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm p-2 sm:p-4" data-testid="calculator-modal">
      <div className="w-full max-w-md bg-slate-950 rounded-2xl border border-cyan-500/30 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-900/80 border-b border-slate-800">
          <div className="flex items-center gap-2 text-cyan-300 font-bold text-sm">
            <CalcIcon className="h-4 w-4" /> Scientific Calculator
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setMode(mode === "deg" ? "rad" : "deg")}
              className="px-2 h-7 rounded-full bg-slate-800 border border-slate-700 text-cyan-300 text-[10px] font-bold uppercase tracking-widest"
              data-testid="calc-mode"
            >
              {mode}
            </button>
            <button onClick={onClose} className="h-7 w-7 rounded-full hover:bg-slate-800 text-slate-400" data-testid="calc-close">
              <X className="h-4 w-4 mx-auto" />
            </button>
          </div>
        </div>

        {/* Display */}
        <div className="p-3 bg-slate-900/40 border-b border-slate-800">
          <input
            ref={inputRef}
            value={expr}
            onChange={(e) => setExpr(e.target.value)}
            placeholder="Type or tap buttons e.g. (12.4+12.5+12.6)/60"
            className="w-full bg-transparent text-right text-slate-100 font-mono text-lg outline-none placeholder:text-slate-600"
            data-testid="calc-expr"
          />
          <div className="flex items-center justify-between mt-1">
            <div className="text-xs text-slate-500 font-mono">
              {history[0] && <>Last: {history[0].expr} = {history[0].value}</>}
            </div>
            <div
              className={`text-right font-mono text-2xl font-black ${preview === "Err" ? "text-rose-400" : "text-cyan-300"}`}
              data-testid="calc-preview"
            >
              {preview || "0"}
            </div>
          </div>
        </div>

        {/* Formula chips (optional, experiment-aware) */}
        {formulas && formulas.length > 0 && (
          <div className="px-2 py-2 bg-slate-900/60 border-b border-slate-800 flex gap-1.5 overflow-x-auto" data-testid="calc-formulas">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold self-center flex-shrink-0 pl-1">Quick formulas</span>
            {formulas.map((f, i) => (
              <button
                key={f.label}
                type="button"
                onClick={() => setExpr(f.template)}
                className="flex-shrink-0 px-3 h-7 rounded-full bg-slate-800 border border-cyan-500/30 text-cyan-300 text-[11px] font-mono hover:bg-cyan-500/20 transition-colors"
                data-testid={`calc-formula-${i}`}
                title={f.template}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Function row */}
        <div className="grid grid-cols-5 gap-1.5 p-2 bg-slate-900/30">
          <Btn onClick={() => append("sin(")} cls="bg-slate-800 text-fuchsia-300 hover:bg-slate-700">sin</Btn>
          <Btn onClick={() => append("cos(")} cls="bg-slate-800 text-fuchsia-300 hover:bg-slate-700">cos</Btn>
          <Btn onClick={() => append("tan(")} cls="bg-slate-800 text-fuchsia-300 hover:bg-slate-700">tan</Btn>
          <Btn onClick={() => append("log(")} cls="bg-slate-800 text-emerald-300 hover:bg-slate-700">log</Btn>
          <Btn onClick={() => append("ln(")} cls="bg-slate-800 text-emerald-300 hover:bg-slate-700">ln</Btn>

          <Btn onClick={() => append("√(")} cls="bg-slate-800 text-cyan-300 hover:bg-slate-700">√</Btn>
          <Btn onClick={() => append("^2")} cls="bg-slate-800 text-cyan-300 hover:bg-slate-700">x²</Btn>
          <Btn onClick={() => append("^")} cls="bg-slate-800 text-cyan-300 hover:bg-slate-700">xʸ</Btn>
          <Btn onClick={() => append("π")} cls="bg-slate-800 text-amber-300 hover:bg-slate-700">π</Btn>
          <Btn onClick={() => append("e")} cls="bg-slate-800 text-amber-300 hover:bg-slate-700">e</Btn>
        </div>

        {/* Number pad */}
        <div className="grid grid-cols-4 gap-1.5 p-2">
          <Btn onClick={clear} cls="bg-rose-600/80 hover:bg-rose-600 text-white" testid="calc-clear">C</Btn>
          <Btn onClick={() => append("(")} cls="bg-slate-800 text-cyan-300 hover:bg-slate-700">(</Btn>
          <Btn onClick={() => append(")")} cls="bg-slate-800 text-cyan-300 hover:bg-slate-700">)</Btn>
          <Btn onClick={back} cls="bg-slate-800 text-amber-300 hover:bg-slate-700" testid="calc-back"><Delete className="h-4 w-4 mx-auto" /></Btn>

          <Btn onClick={() => append("7")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">7</Btn>
          <Btn onClick={() => append("8")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">8</Btn>
          <Btn onClick={() => append("9")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">9</Btn>
          <Btn onClick={() => append("/")} cls="bg-cyan-600/80 hover:bg-cyan-600 text-white">÷</Btn>

          <Btn onClick={() => append("4")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">4</Btn>
          <Btn onClick={() => append("5")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">5</Btn>
          <Btn onClick={() => append("6")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">6</Btn>
          <Btn onClick={() => append("*")} cls="bg-cyan-600/80 hover:bg-cyan-600 text-white">×</Btn>

          <Btn onClick={() => append("1")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">1</Btn>
          <Btn onClick={() => append("2")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">2</Btn>
          <Btn onClick={() => append("3")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">3</Btn>
          <Btn onClick={() => append("-")} cls="bg-cyan-600/80 hover:bg-cyan-600 text-white">−</Btn>

          <Btn onClick={() => append("0")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600 col-span-2">0</Btn>
          <Btn onClick={() => append(".")} cls="bg-slate-700 text-slate-100 hover:bg-slate-600">.</Btn>
          <Btn onClick={() => append("+")} cls="bg-cyan-600/80 hover:bg-cyan-600 text-white">+</Btn>

          <Btn onClick={copyValue} cls="bg-slate-800 text-emerald-300 hover:bg-slate-700" testid="calc-copy">
            <div className="flex items-center justify-center gap-1 text-sm"><Copy className="h-3.5 w-3.5" /> Copy</div>
          </Btn>
          <Btn onClick={equals} cls="bg-emerald-600 hover:bg-emerald-500 text-white col-span-3 font-bold" testid="calc-equals">
            <div className="flex items-center justify-center gap-1"><ChevronsRight className="h-4 w-4" /> = {preview && preview !== "Err" ? preview : ""}</div>
          </Btn>
        </div>
      </div>
    </div>
  );
};

export default Calculator;
